import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ParkingLotMap, {
    ParkingLotMapOverlayMarker,
    ParkingLotMapViewport,
    ParkingLotMapViewportTarget
} from '../../components/ParkingLotMap';
import ParkingLotMapSearch from '../../components/ParkingLotMapSearch';
import ParkingLotMapSidebar from '../../components/ParkingLotMapSidebar';
import BulkEditPanel, { BulkParkingTypeFilter } from '../../components/BulkEditPanel';
import { ParkingLot } from '../../lib/types';
import {
    useParkingLotsMapView,
    useParkingLotsMapContextLayer,
    getParkingLotByIdLegacy,
    updateParkingLot,
    PlaceSearchLocation
} from '../../lib/api';
import type { ParkingLotsEditorFiltersState } from '../../lib/parkingLotsEditorFilters';
import {
    GoogleMapsLatLng,
    convertGeoJSONToGoogleMaps,
    extractGeometryPoints,
    normalizeMapItemToParkingLot
} from '../../lib/utils';
import {
    DENSE_CITY_MIN_LOT_ZOOM,
    expandMapViewportBbox,
    isMapViewportResponseTruncated,
    shouldLoadIndividualLots,
    shouldRefreshMapViewport,
    TRUNCATED_MAP_VIEWPORT_PADDING,
} from '../../lib/mapViewport';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import {
    MapPin,
    Edit2,
    Plus,
    X,
    Maximize,
    Minimize,
    RectangleHorizontal,
    Loader2,
    AlertTriangle,
    ZoomIn,
} from 'lucide-react';

interface ParkingLotsMapViewProps {
    selectedCity: string;
    cities: { id: string; name: string; center: [number, number] }[];
    editorFilters?: ParkingLotsEditorFiltersState;
}

/** Muted styling for lots that only appear in the context layer (not in the primary filter). */
const CONTEXT_POLYGON_STYLE: Partial<google.maps.PolygonOptions> = {
    fillOpacity: 0.2,
    strokeOpacity: 0.35,
    strokeWeight: 2,
    zIndex: 0,
};

type BufferedMapViewport = ParkingLotMapViewport & {
    city: string;
    enabled: boolean;
};

const ParkingLotsMapView: React.FC<ParkingLotsMapViewProps> = ({ selectedCity, cities, editorFilters }) => {
    const [selectedPlace, setSelectedPlace] = useState<ParkingLot | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [creatingNew, setCreatingNew] = useState(false);
    const [currentCoordinates, setCurrentCoordinates] = useState<GoogleMapsLatLng[]>([]);
    const [showSidebar, setShowSidebar] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [hiddenInMapView, setHiddenInMapView] = useState<Set<string>>(() => new Set());
    const [lastClickTime, setLastClickTime] = useState<number>(0);
    const [lastClickIndex, setLastClickIndex] = useState<number>(-1);
    const doubleClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
    const [mapViewport, setMapViewport] = useState<BufferedMapViewport | null>(null);
    const [searchResult, setSearchResult] = useState<{
        location: PlaceSearchLocation;
        label: string;
        key: string;
    } | null>(null);
    const searchSequenceRef = useRef(0);

    // Bulk select state
    const [bulkSelectMode, setBulkSelectMode] = useState(false);
    const [bulkSelectedLotIds, setBulkSelectedLotIds] = useState<Set<string>>(new Set());
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const [bulkError, setBulkError] = useState<string | null>(null);

    const addToHidden = useCallback((lotId: string) => {
        setHiddenInMapView(prev => new Set(prev).add(lotId));
    }, []);
    const clearHidden = useCallback(() => {
        setHiddenInMapView(() => new Set());
    }, []);

    const viewportForRequest = useMemo(() => ({
        bbox: mapViewport?.bbox,
        zoom: mapViewport?.zoom,
        enabled: mapViewport?.city === selectedCity && mapViewport.enabled,
    }), [mapViewport, selectedCity]);
    const {
        rawMapItems,
        meta: primaryMeta,
        isLoading: primaryLoading,
        isValidating: primaryValidating,
        error: primaryError,
        mutate
    } = useParkingLotsMapView(
        selectedCity,
        editorFilters,
        viewportForRequest
    );
    const {
        rawContextItems,
        isLoading: contextLoading,
        isValidating: contextValidating,
        error: contextError,
        mutate: mutateContext
    } = useParkingLotsMapContextLayer(selectedCity, editorFilters, viewportForRequest);

    const primaryResponseTruncated = isMapViewportResponseTruncated(primaryMeta?.total, rawMapItems.length);
    const requiresCloserZoom = mapViewport?.city === selectedCity && !mapViewport.enabled;

    const handleViewportChange = useCallback((viewport: ParkingLotMapViewport) => {
        setMapViewport((current) => {
            const shouldLoad = shouldLoadIndividualLots(selectedCity, viewport.zoom);
            if (!shouldLoad) {
                if (current?.city === selectedCity && !current.enabled) return current;
                return {
                    bbox: viewport.bbox,
                    zoom: viewport.zoom,
                    city: selectedCity,
                    enabled: false,
                };
            }

            if (
                current?.city === selectedCity &&
                current.enabled &&
                !shouldRefreshMapViewport(current.bbox, viewport.bbox, primaryResponseTruncated)
            ) {
                return current;
            }

            return {
                bbox: expandMapViewportBbox(
                    viewport.bbox,
                    primaryResponseTruncated ? TRUNCATED_MAP_VIEWPORT_PADDING : undefined
                ),
                zoom: viewport.zoom,
                city: selectedCity,
                enabled: true,
            };
        });
    }, [primaryResponseTruncated, selectedCity]);

    const cityViewportTarget = useMemo<ParkingLotMapViewportTarget | null>(() => {
        const city = cities.find((candidate) => candidate.id === selectedCity);
        if (!city) return null;
        return {
            key: `city-${city.id}`,
            position: { lat: city.center[1], lng: city.center[0] },
            zoom: city.id === 'copenhagen' || city.id === 'frederiksberg' ? 15.5 : 13,
        };
    }, [cities, selectedCity]);
    const searchViewportTarget = useMemo<ParkingLotMapViewportTarget | null>(() => (
        searchResult
            ? {
                key: searchResult.key,
                position: { lat: searchResult.location.lat, lng: searchResult.location.lng },
                zoom: 16,
            }
            : null
    ), [searchResult]);
    const activeViewportTarget = searchViewportTarget ?? (
        mapViewport?.city === selectedCity ? null : cityViewportTarget
    );
    const searchMarkers = useMemo<ParkingLotMapOverlayMarker[]>(() => (
        searchResult
            ? [{
                id: 'parking-lot-address-search',
                position: { lat: searchResult.location.lat, lng: searchResult.location.lng },
                title: searchResult.label,
                kind: 'address',
                zIndex: 2500,
            }]
            : []
    ), [searchResult]);

    const mapContextMode = editorFilters?.mapContextMode ?? 'none';
    const isContextMode = mapContextMode !== 'none';

    const { parkingLotsForMap, polygonStylesByLotId: basePolygonStyles, primaryCount, contextCount } = useMemo(() => {
        const primary = rawMapItems.map(normalizeMapItemToParkingLot) as ParkingLot[];
        const primaryIds = new Set(primary.map((p) => p.lotId));
        if (!isContextMode || rawContextItems.length === 0) {
            return {
                parkingLotsForMap: primary,
                polygonStylesByLotId: undefined as Record<string, Partial<google.maps.PolygonOptions>> | undefined,
                primaryCount: primary.length,
                contextCount: 0,
            };
        }
        const contextNorm = rawContextItems.map(normalizeMapItemToParkingLot) as ParkingLot[];
        const contextOnly = contextNorm.filter((lot) => !primaryIds.has(lot.lotId));
        const styles: Record<string, Partial<google.maps.PolygonOptions>> = {};
        for (const lot of contextOnly) {
            styles[lot.lotId] = CONTEXT_POLYGON_STYLE;
        }
        return {
            parkingLotsForMap: [...primary, ...contextOnly],
            polygonStylesByLotId: styles,
            primaryCount: primary.length,
            contextCount: contextOnly.length,
        };
    }, [rawMapItems, rawContextItems, isContextMode]);

    const polygonStylesByLotId = useMemo(() => {
        if (bulkSelectedLotIds.size === 0) return basePolygonStyles;
        const merged: Record<string, Partial<google.maps.PolygonOptions>> = { ...(basePolygonStyles ?? {}) };
        bulkSelectedLotIds.forEach((lotId) => {
            merged[lotId] = {
                ...merged[lotId],
                fillColor: '#f59e0b',
                strokeColor: '#b45309',
                fillOpacity: 0.65,
                strokeWeight: 4,
                strokeOpacity: 1,
                zIndex: 500,
            };
        });
        return merged;
    }, [basePolygonStyles, bulkSelectedLotIds]);

    const isLoading = primaryLoading;
    const isUpdating = !isLoading && (
        primaryValidating ||
        (isContextMode && (contextLoading || contextValidating))
    );
    const selectedGeometryIsMetadataOnly = selectedPlace?.geometry?.type === 'Point' ||
        selectedPlace?.geometry?.type === 'LineString' ||
        selectedPlace?.geometry?.type === 'MultiLineString';
    const mutateAll = useCallback(() => {
        void mutate();
        void mutateContext();
    }, [mutate, mutateContext]);

    const handleBulkRectDrawn = useCallback((north: number, south: number, east: number, west: number) => {
        const inside = parkingLotsForMap.filter((lot) => {
            if (!lot.geometry) return false;
            const paths = extractGeometryPoints(lot.geometry);
            if (paths.length === 0) return false;
            const avgLat = paths.reduce((s, p) => s + p.lat, 0) / paths.length;
            const avgLng = paths.reduce((s, p) => s + p.lng, 0) / paths.length;
            return avgLat <= north && avgLat >= south && avgLng <= east && avgLng >= west;
        });
        setBulkSelectedLotIds(new Set(inside.map((l) => l.lotId)));
        setBulkError(null);
    }, [parkingLotsForMap]);

    const handleBulkApply = useCallback(async (patch: { displayInApp: boolean }, typeFilter: BulkParkingTypeFilter) => {
        if (bulkSelectedLotIds.size === 0) return;
        setIsBulkSaving(true);
        setBulkError(null);
        try {
            const lotMap = new Map(parkingLotsForMap.map((l) => [l.lotId, l]));
            const ids: string[] = [];
            bulkSelectedLotIds.forEach((id) => {
                const lot = lotMap.get(id);
                const pt = lot?.parking_type ?? 'free';
                if (typeFilter.mode === 'all') {
                    ids.push(id);
                } else if (typeFilter.mode === 'include') {
                    if (typeFilter.types.includes(pt)) ids.push(id);
                } else if (typeFilter.mode === 'exclude') {
                    if (!typeFilter.types.includes(pt)) ids.push(id);
                }
            });
            await Promise.all(ids.map((id) => updateParkingLot(id, patch)));
            mutateAll();
            setBulkSelectedLotIds(new Set());
        } catch (e) {
            setBulkError(e instanceof Error ? e.message : 'Der opstod en fejl');
        } finally {
            setIsBulkSaving(false);
        }
    }, [bulkSelectedLotIds, parkingLotsForMap, mutateAll]);

    const enterBulkMode = useCallback(() => {
        setEditMode(false);
        setCreatingNew(false);
        setCurrentCoordinates([]);
        setBulkSelectMode(true);
        setBulkSelectedLotIds(new Set());
        setBulkError(null);
        setShowSidebar(false);
        setSelectedPlace(null);
    }, []);

    const exitBulkMode = useCallback(() => {
        setBulkSelectMode(false);
        setBulkSelectedLotIds(new Set());
        setBulkError(null);
    }, []);

    useEffect(() => {
        setMapViewport(null);
        setSelectedPlace(null);
        setShowSidebar(false);
        setEditMode(false);
        setCreatingNew(false);
        setCurrentCoordinates([]);
        setHiddenInMapView(new Set());
        setBulkSelectMode(false);
        setBulkSelectedLotIds(new Set());
        setSearchResult(null);
    }, [selectedCity]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === fullscreenContainerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Reset state when exiting edit mode
    useEffect(() => {
        if (!editMode) {
            setCreatingNew(false);
            setCurrentCoordinates([]);
        }
    }, [editMode]);

    // Load coordinates when selecting a place for editing
    useEffect(() => {
        if (
            selectedPlace &&
            editMode &&
            !creatingNew &&
            (selectedPlace.geometry?.type === 'Polygon' || selectedPlace.geometry?.type === 'MultiPolygon')
        ) {
            const coords = convertGeoJSONToGoogleMaps(selectedPlace.geometry);
            setCurrentCoordinates(coords);
        }
    }, [selectedPlace, editMode, creatingNew]);

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (bulkSelectMode) return;
        const selectedGeometryIsPolygon = selectedPlace?.geometry?.type === 'Polygon' ||
            selectedPlace?.geometry?.type === 'MultiPolygon';
        if (editMode && (creatingNew || selectedGeometryIsPolygon)) {
            if (event.latLng) {
                const newPoint: GoogleMapsLatLng = {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                };
                setCurrentCoordinates(prev => [...prev, newPoint]);
            }
        }
    }, [editMode, creatingNew, selectedPlace, bulkSelectMode]);

    const handlePlaceSelect = useCallback((place: ParkingLot) => {
        if (!editMode) {
            setShowSidebar(true);
            const id = place._id || (place as { lotId?: string }).lotId;
            if (!id) {
                setSelectedPlace(place);
                return;
            }
            setDetailLoading(true);
            setSelectedPlace(null);
            getParkingLotByIdLegacy(id)
                .then((full) => {
                    setSelectedPlace({ ...full, _id: full.lotId || full._id });
                })
                .catch(() => {
                    setSelectedPlace(place);
                })
                .finally(() => {
                    setDetailLoading(false);
                });
        }
    }, [editMode]);

    const handlePolygonEdit = useCallback((place: ParkingLot, newPaths: GoogleMapsLatLng[]) => {
        if (!editMode) return;
        if (creatingNew) {
            setCurrentCoordinates(newPaths);
            return;
        }
        if (selectedPlace?._id === place._id) {
            setCurrentCoordinates(newPaths);
        }
    }, [editMode, creatingNew, selectedPlace]);

    const handleMarkerDragEnd = useCallback((index: number, newPosition: GoogleMapsLatLng) => {
        setCurrentCoordinates(prev => {
            const updated = [...prev];
            updated[index] = newPosition;
            return updated;
        });
    }, []);

    // Handle marker click with double-click detection
    const handleMarkerClick = useCallback((index: number) => {
        const now = Date.now();
        const timeDiff = now - lastClickTime;
        const isDoubleClick = timeDiff < 300 && lastClickIndex === index;

        if (doubleClickTimeoutRef.current) {
            clearTimeout(doubleClickTimeoutRef.current);
        }

        if (isDoubleClick) {
            // Double-click detected - remove marker
            if (currentCoordinates.length > 4) {
                setCurrentCoordinates(prev => prev.filter((_, i) => i !== index));
            }
            setLastClickTime(0);
            setLastClickIndex(-1);
        } else {
            setLastClickTime(now);
            setLastClickIndex(index);
            doubleClickTimeoutRef.current = setTimeout(() => {
                setLastClickTime(0);
                setLastClickIndex(-1);
            }, 300);
        }
    }, [lastClickTime, lastClickIndex, currentCoordinates.length]);

    const handleCreateNew = () => {
        exitBulkMode();
        setEditMode(true);
        setCreatingNew(true);
        setSelectedPlace(null);
        setCurrentCoordinates([]);
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedPlace) {
            exitBulkMode();
            setEditMode(true);
            const geometryIsPolygon = selectedPlace.geometry?.type === 'Polygon' ||
                selectedPlace.geometry?.type === 'MultiPolygon';
            setCurrentCoordinates(geometryIsPolygon ? convertGeoJSONToGoogleMaps(selectedPlace.geometry) : []);
        }
    };

    const handleSave = (savedPlace: ParkingLot) => {
        mutateAll();
        setEditMode(false);
        setCreatingNew(false);
        setSelectedPlace(savedPlace);
        setCurrentCoordinates([]);
    };

    const handleDelete = (placeId: string) => {
        mutateAll();
        setSelectedPlace(null);
        setShowSidebar(false);
        setEditMode(false);
        setCreatingNew(false);
        setCurrentCoordinates([]);
    };

    const handleCancel = () => {
        setEditMode(false);
        setCreatingNew(false);
        setCurrentCoordinates([]);
        if (!selectedPlace) {
            setShowSidebar(false);
        }
    };

    const handleImportComplete = () => {
        mutateAll();
    };

    const handleCloseSidebar = () => {
        if (!editMode) {
            setShowSidebar(false);
            setSelectedPlace(null);
        }
    };

    const toggleFullscreen = async () => {
        const container = fullscreenContainerRef.current;
        if (!container) return;

        if (document.fullscreenElement === container) {
            await document.exitFullscreen();
            return;
        }

        await container.requestFullscreen();
    };

    return (
            <div
                ref={fullscreenContainerRef}
                className={`h-full flex flex-col bg-white ${isFullscreen ? 'fixed inset-0 z-[70]' : ''}`}
            >
            {/* Header with controls */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <MapPin className="h-5 w-5 text-gray-600" />
                    <h1 className="text-lg font-semibold text-gray-900">Kort Visning</h1>
                    <span className="text-sm text-gray-500">
                        {requiresCloserZoom ? (
                            <>Zoom ind for at hente pladser</>
                        ) : isContextMode ? (
                            <>
                                {primaryCount} {primaryCount === 1 ? 'primær' : 'primære'} · {contextCount} kontekst
                            </>
                        ) : (
                            <>
                                {parkingLotsForMap.length} {parkingLotsForMap.length === 1 ? 'plads' : 'pladser'}
                            </>
                        )}
                    </span>
                    {isContextMode && (
                        <span className="text-xs text-gray-500">Primær = fuld farve · Kontekst = dæmpet</span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => void toggleFullscreen()}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                    >
                        {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                        {isFullscreen ? 'Luk fuld skærm' : 'Fuld skærm'}
                    </button>
                    {hiddenInMapView.size > 0 && (
                        <button
                            onClick={clearHidden}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
                        >
                            Vis alle polygoner
                        </button>
                    )}
                    {!editMode && !bulkSelectMode && (
                        <>
                            <button
                                onClick={enterBulkMode}
                                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                            >
                                <RectangleHorizontal className="h-4 w-4" />
                                Markér område
                            </button>
                            <button
                                onClick={handleCreateNew}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Opret Ny Plads
                            </button>
                            {selectedPlace && (
                                <button
                                    onClick={handleEdit}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                                >
                                    <Edit2 className="h-4 w-4" />
                                    Rediger
                                </button>
                            )}
                        </>
                    )}
                    {editMode && (
                        <div className="bg-yellow-100 border border-yellow-300 rounded-md px-3 py-2 flex items-center gap-2">
                            <Edit2 className="h-4 w-4 text-yellow-800" />
                            <span className="text-sm font-medium text-yellow-800">Redigerings Mode</span>
                            <span className="text-xs text-yellow-700">
                                {currentCoordinates.length} {currentCoordinates.length === 1 ? 'punkt' : 'punkter'}
                            </span>
                        </div>
                    )}
                    {bulkSelectMode && (
                        <div className="bg-amber-100 border border-amber-300 rounded-md px-3 py-2 flex items-center gap-2">
                            <RectangleHorizontal className="h-4 w-4 text-amber-800" />
                            <span className="text-sm font-medium text-amber-800">
                                {bulkSelectedLotIds.size > 0
                                    ? `${bulkSelectedLotIds.size} pladser valgt`
                                    : 'Træk et område på kortet'}
                            </span>
                            <button onClick={exitBulkMode} className="text-amber-700 hover:text-amber-900 ml-1">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isContextMode && contextError && (
                <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                    Kontekst-lag kunne ikke indlæses ({contextError.message}). Kortet viser kun det primære filter.
                </div>
            )}

            {/* Map and Sidebar Container */}
            <div className={`relative flex-1 flex overflow-hidden ${isFullscreen ? 'min-h-0' : ''}`}>
                {/* Map */}
                <div className="flex-1 relative">
                    <ParkingLotMapSearch
                        key={selectedCity}
                        selectedCity={selectedCity}
                        onSelect={(location, label) => {
                            searchSequenceRef.current += 1;
                            setSearchResult({
                                location,
                                label,
                                key: `search-${searchSequenceRef.current}`,
                            });
                        }}
                        onClear={() => setSearchResult(null)}
                    />
                    <ParkingLotMap
                        parkingLots={parkingLotsForMap}
                        selectedPlace={selectedPlace}
                        editMode={editMode}
                        creatingNew={creatingNew}
                        currentCoordinates={currentCoordinates}
                        viewportTarget={activeViewportTarget}
                        onViewportChange={handleViewportChange}
                        autoFitBounds={false}
                        overlayMarkers={searchMarkers}
                        hiddenInMapView={hiddenInMapView}
                        polygonStylesByLotId={polygonStylesByLotId}
                        bulkSelectMode={bulkSelectMode}
                        onMapClick={handleMapClick}
                        onPlaceSelect={handlePlaceSelect}
                        onPolygonEdit={handlePolygonEdit}
                        onMarkerDragEnd={handleMarkerDragEnd}
                        onMarkerClick={handleMarkerClick}
                        onBulkRectDrawn={handleBulkRectDrawn}
                    />
                    {isLoading && (
                        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/65">
                            <LoadingState message="Indlæser synlige parkeringspladser..." />
                        </div>
                    )}
                    {requiresCloserZoom ? (
                        <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border border-indigo-200 bg-white/95 px-4 py-2 text-sm text-gray-700 shadow-md">
                            <span className="flex items-center gap-2">
                                <ZoomIn className="h-4 w-4 text-indigo-600" />
                                Zoom ind til niveau {DENSE_CITY_MIN_LOT_ZOOM} for at hente individuelle pladser
                            </span>
                        </div>
                    ) : isUpdating ? (
                        <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border border-gray-200 bg-white/95 px-3 py-2 text-sm text-gray-600 shadow-md">
                            <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                                Opdaterer synligt område...
                            </span>
                        </div>
                    ) : primaryResponseTruncated ? (
                        <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border border-amber-300 bg-amber-50/95 px-4 py-2 text-sm text-amber-900 shadow-md">
                            <span className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" />
                                Viser {rawMapItems.length.toLocaleString()} af {primaryMeta?.total.toLocaleString()} pladser i området – zoom tættere ind
                            </span>
                        </div>
                    ) : null}
                    {primaryError && (
                        <div className="absolute left-4 top-4 z-20 max-w-md">
                            <ErrorState error={primaryError.message} onRetry={() => mutateAll()} />
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                {showSidebar && (
                    <div
                        className={
                            isFullscreen
                                ? 'absolute top-0 right-0 bottom-0 w-[30rem] max-w-[92vw] border-l border-gray-200 bg-white shadow-2xl z-20'
                                : 'w-96 border-l border-gray-200 flex-shrink-0'
                        }
                    >
                        {detailLoading ? (
                            <div className="p-4 flex items-center justify-center h-32">
                                <span className="text-sm text-gray-500">Indlæser detaljer...</span>
                            </div>
                        ) : (
                        <ParkingLotMapSidebar
                            selectedPlace={selectedPlace}
                            editMode={editMode}
                            creatingNew={creatingNew}
                            currentCoordinates={currentCoordinates}
                            hiddenInMapView={hiddenInMapView}
                            onClose={handleCloseSidebar}
                            onSave={handleSave}
                            onDelete={handleDelete}
                            onCancel={handleCancel}
                            onImportComplete={handleImportComplete}
                            onHidePolygon={addToHidden}
                            onShowAllPolygons={clearHidden}
                            availableLots={parkingLotsForMap}
                        />
                        )}
                    </div>
                )}
            </div>

            {/* Bulk edit panel */}
            {bulkSelectMode && (
                <BulkEditPanel
                    selectedLots={parkingLotsForMap.filter((l) => bulkSelectedLotIds.has(l.lotId))}
                    isSaving={isBulkSaving}
                    error={bulkError}
                    onApply={handleBulkApply}
                    onClearSelection={() => { setBulkSelectedLotIds(new Set()); setBulkError(null); }}
                    onExit={exitBulkMode}
                />
            )}

            {/* Instructions overlay when in edit mode */}
            {editMode && (
                <div className="absolute bottom-4 left-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-sm">
                    <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-900">Redigerings Instruktioner</h3>
                        <button
                            onClick={() => setEditMode(false)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <ul className="text-xs text-gray-600 space-y-1">
                        {creatingNew ? (
                            <>
                                <li>• Klik på kortet for at tilføje punkter</li>
                                <li>• Træk punkter/kanter på polygonet for at justere</li>
                                <li>• Double-klik på et punkt (lilla) for at fjerne det</li>
                                <li>• Minimum 3 punkter påkrævet</li>
                            </>
                        ) : selectedGeometryIsMetadataOnly ? (
                            <>
                                <li>• Geometrien bevares uændret</li>
                                <li>• Redigér metadata i panelet til højre</li>
                            </>
                        ) : (
                            <>
                                <li>• Træk polygonens håndtag for at justere kanter</li>
                                <li>• Klik på en kant for at tilføje et nyt punkt</li>
                                <li>• Højreklik på et punkt for at fjerne det (Google Maps standard)</li>
                            </>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ParkingLotsMapView;
